import {
    DollarSign,
    Package,
    ArrowUpRight,
    RefreshCcw,
    Activity,
    Eye,
    Truck, 
    XCircle, 
    CheckCircle, 
    FilePlus2,
    PackageCheck,
    HelpCircle,
    Undo2,
    Ban,
    Copy,
    User,
    Wallet,
    Headset, ScanLine, ArrowDown 
} from "lucide-react"
import { useState } from "react";
import { Link } from "react-router-dom"; 
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import Loading from "../../../cors/Loading";
import { toast } from 'sonner';
import BaarChart from "../../../ui/BaarChart";
import LinneChart from "../../../ui/LinneChart";
import RadialChart from "../../../ui/RadialChart";
import BaarChartConfirmator from "../../../ui/BaarChartConfirmator";
import SelectDate from "../../../cors/SelectDate";
import Select from "react-select";
import axios from "axios"; 
import Map from "../../../cors/Map";

export default function DashboardAdmin() {
  const { token, role, roles } = useAuth();
  const [state, setState] = useState({
    total_revenue: 0,
    total_profit_sellers: 0,
    total_profit_confirmator: 0,
    total_profit: 0,
    total_orders: 0,
    orders_status: {},
    top_products: [],
    top_sellers: []
  });
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [loadingSellers, setLoadingSellers] = useState(false);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setLoading(true);
    setShowContent(false);
    const sellerId = selectedSeller && selectedSeller.value !== '' ? selectedSeller.value : null;
    fetchStats(range, sellerId);
  };

  const handleSellerChange = (selectedOption) => {
    setSelectedSeller(selectedOption);
    setLoading(true);
    setShowContent(false);
    
    if (selectedOption && selectedOption.value !== '') {
      fetchStats(dateRange, selectedOption.value);
    } else {
      fetchStats(dateRange, null);
    }
  };
  
  const fetchStats = async (range, sellerId = null) => {
    try {
      const requestBody = { range };
      if (sellerId) {
        requestBody.seller_id = sellerId;
      }

      const response = await fetch(BASE_URL + '/api/dashboard-stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.code === 200 && result.data) {
        setState(result.data);
        setTimeout(() => setShowContent(true), 100);
      } else {
        console.error("Unexpected API response structure:", result);
        toast.error("Unexpected data format received from API.");
        setShowContent(true); 
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error(err.message || "Error loading dashboard statistics.");
      setShowContent(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchSellers = async () => {
    try {
      setLoadingSellers(true);
      const res = await axios.get(`${BASE_URL}/api/search-saller`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const sellers = res.data.data;

      const sellerOptions = [
        { value: '', label: 'Tous les vendeurs' },
        ...sellers.map((s) => ({
          value: s.id,
          label: `${s.name ?? "Unknown"} (${s.name_store ?? "No Store"}) - ${s.status}`,
        }))
      ];

      setOptions(sellerOptions);
      setSelectedSeller(sellerOptions[0]);
    } catch (err) {
      console.error("Error fetching sellers:", err);
      toast.error("Erreur lors du chargement des vendeurs");
      setOptions([{ value: '', label: 'Tous les vendeurs' }]);
      setSelectedSeller({ value: '', label: 'Tous les vendeurs' });
    } finally {
      setLoadingSellers(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "nouveau":
        return "bg-blue-100 text-blue-700 border border-blue-200";
      case "confirme":
        return "bg-green-100 text-green-700 border border-green-200";
      case "suivi":
        return "bg-amber-100 text-amber-700 border border-amber-200";
      case "reporte":
        return "bg-yellow-100 text-yellow-700 border border-yellow-200";
      case "indelivery":
        return "bg-indigo-100 text-indigo-700 border border-indigo-200";
      case "livre":
        return "bg-emerald-100 text-emerald-700 border border-emerald-200";
      case "retour":
        return "bg-orange-100 text-orange-700 border border-orange-200";
      case "refuse":
        return "bg-red-100 text-red-700 border border-red-200";
      case "annule":
        return "bg-gray-200 text-gray-700 border border-gray-300";
      case "duplicated":
        return "bg-pink-100 text-pink-700 border border-pink-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "nouveau":
        return "Nouvelle";
      case "confirme":
        return "Confirmé";
      case "reporte":
        return "Attente de confirmation";
      case "suivi":
        return "En suivi";
      case "indelivery":
        return "En livraison";
      case "livre":
        return "Livré";
      case "retour":
        return "Returné";
      case "refuse":
        return "Refusé";
      case "annule":
        return "Annulé";
      case "duplicated":
        return "Dupliqué";
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    const iconSize = 40;
    switch (status) {
      case "nouveau":
        return <FilePlus2 size={iconSize} />;
      case "confirme":
        return <CheckCircle size={iconSize} />;
      case "suivi":
        return <Headset size={iconSize} />;
      case "reporte":
        return <HelpCircle size={iconSize} />;
      case "indelivery":
        return <Truck size={iconSize} />;
      case "livre":
        return <PackageCheck size={iconSize} />;
      case "retour":
        return <Undo2 size={iconSize} />;
      case "refuse":
        return <XCircle size={iconSize} />;
      case "annule":
        return <Ban size={iconSize} />;
      case "duplicated":
        return <Copy size={iconSize} />;
      default:
        return null;
    }
  }

  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  const stats = [
    {
      title: "Chiffre d'affaires",
      value: `${formatMoney(state?.total_revenue)} MAD`,
      initialValue: "0.00 MAD",
      change: "+20.1%", 
      icon: DollarSign,
      color: "bg-green-500",
    },
    {
      title: "Bénéfice des vendeurs",
      value: `${formatMoney(state?.total_profit_sellers)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: User, 
      color: "bg-orange-500",
    },
    {
      title: "Bénéfice de confirmateur",
      value: `${formatMoney(state?.total_profit_confirmator)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: Headset, 
      color: "bg-yellow-500",
    },
    {
      title: "Profit Net",
      value: `${formatMoney(state?.total_profit)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: Wallet, 
      color: "bg-teal-500",
    },
  ];

  const topProducts = (state.top_products?.length > 0 ? state.top_products : Array(5).fill({
    name: "Chargement...",
    image_py: "",
    total_quantity: "0",
    total_revenue: "0.00"
  })).map(product => ({
    name: product.name,
    image: product.image_py,
    sales: parseInt(product.total_quantity) || 0,
    revenue: `${formatMoney(product.total_revenue)} MAD`,
    progress: Math.min(85, ((parseInt(product.total_quantity) || 0) / (state?.total_orders || 1)) * 100),
  }));

  const topSallers = (state.top_sellers?.length > 0 ? state.top_sellers : Array(5).fill({
    name: "Chargement...",
    name_store: "",
    total_orders: "0",
    total_revenue: "0.00"
  })).map(saller => ({
    name: saller.seller_name,
    name_store: saller.store_name,
    total_orders: saller.total_orders,
    revenue: `${formatMoney(saller.total_revenue)} MAD`,
    progress: Math.min(85, ((parseInt(saller.total_orders) || 0) / (state?.total_orders || 1)) * 100),
  }));

  return (
    <div className="min-h-screen">
      <div className="flex">
        <main className="flex-1 md:ml-0">
          <div className="py-8">
            <div className="mx-auto px-4 sm:px-6 px-5">
              <div className={`mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between transition-all duration-200 `}>
                <div>
                  <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
                    Tableau de bord
                  </h1>
                  <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
                    Bon retour ! Voici ce qui se passe dans votre entreprise.
                  </p>
                </div>
                <div
                 onClick={  fetchSellers }
                 className="flex max-xl:grid items-center gap-3 max-lg:mt-10">
                    <Select
                    instanceId={1}
                    options={options}
                    value={selectedSeller}
                    isLoading={loadingSellers}
                    placeholder="Rechercher un vendeur..."
                    className="text-xs rounded-2xl shadow-sm  border-0 outline-none min-w-64"
                    onChange={handleSellerChange}
                    isClearable={false}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        cursor:'pointer',
                        border: 'none',             
                        borderRadius: '15px',     
                        minWidth: '16rem',  
                        boxShadow: state.isFocused 
                          ? '0 2px 6px rgba(0,0,0,0.15)'  
                          : '0 2px 6px rgba(0,0,0,0.15)',
                        borderRadius: '16px',
                        boxShadow:'none',
                        outline: 'none',
                        '&:hover': {
                          border: 'none'
                        }
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: 4,
                      }),
                      clearIndicator: (provided) => ({
                        ...provided,
                        padding: 4,
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        padding: '2px 13px'
                      }),
                    }}
                  />
                  <SelectDate onDateRangeChange={handleDateRangeChange} />
                  <button 
                    className="flex items-center gap-1 max-w-max rounded-xl bg-white shadow-sm py-2.5 px-4 cursor-pointer text-xs transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      setLoading(true);
                      setShowContent(false);
                      const sellerId = selectedSeller && selectedSeller.value !== '' ? selectedSeller.value : null;
                      fetchStats(dateRange, sellerId);
                    }}
                  >
                    <RefreshCcw 
                      className={`w-3.5 h-3.5 transition-transform duration-500 ease-in-out ${
                        loading ? 'animate-spin' : ''
                      }`} 
                    />
                    <span>{loading ? 'Actualisation...' : 'Actualiser'}</span>
                  </button>
                </div>
              </div>
              {
                role === 1 || (role === 2 && roles.view_statistics === 1) ?
                  <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4 mb-8 transition-all duration-1000 `}>
                    {stats.map((stat, index) => (
                      <div
                        key={stat.title}
                        className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-6 `}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                            <p className="text-2xl font-bold text-gray-900 transition-all duration-700">
                              {showContent ? stat.value : stat.initialValue}
                            </p>
                            <div className="flex items-center mt-2">
                              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                              <span className="text-sm font-medium text-green-600">{stat.change}</span>
                              <span className="text-sm text-gray-500 ml-1">vs last month</span>
                            </div>
                          </div>
                          <div className={`${stat.color} p-3 rounded-xl shadow-lg transition-all duration-500`}>
                            <stat.icon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  :
                  ''
              }
                <div>
                  {
                    loading ?
                    <div className="flex justify-center mt-52">
                      <Loading /> 
                    </div>
                    :
                    <div>
                      <div className={`mb-10 grid grid-cols-5 max-lg:grid-cols-2 gap-5 max-lg:gap-x-3.5 max-lg:gap-y-4 transition-all duration-700 delay-300 ${
                        showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}>
                      
                        <Link
                          to="/orders/tout"
                          className={`
                            relative overflow-hidden rounded-xl
                            bg-emerald-100 border border-emerald-200
                            text-emerald-500 shadow-md
                            transition-all duration-300
                            hover:shadow-xl hover:scale-[1.03]
                            ${showContent ? "scale-100" : "scale-95"}
                          `}
                        >
                          <svg
                              className="absolute inset-0 w-full h-full opacity-30"
                              viewBox="0 0 400 200"
                              fill="none"
                            >
                              <path
                                d="M280 0 C340 40 360 80 400 120"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                              <path
                                d="M120 200 C160 140 220 100 300 80"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                            </svg>

                          <div className="relative px-5 max-lg:px-2 py-2 flex flex-col justify-between h-28">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium opacity-90">
                                Tout
                              </div>

                              <Package size={40} className="opacity-90" />
                            </div>

                            <div className="text-2xl font-semibold">
                              {formatCount(state?.total_orders) || 0}
                            </div>
                          </div>
                        </Link>

                        {   
                          Object.entries(state?.orders_status || {}).map(([statusName, count], index) => (
                            <Link
                                key={index}
                                to={`/orders/${statusName}`}
                                className={`
                                  relative overflow-hidden rounded-xl
                                  shadow-md
                                  transition-all duration-300
                                  hover:shadow-xl hover:scale-[1.03]
                                  ${getStatusColor(statusName)}
                                  ${showContent ? "scale-100" : "scale-95"}
                                `}
                              >
                                <svg
                              className="absolute inset-0 w-full h-full opacity-30"
                              viewBox="0 0 400 200"
                              fill="none"
                            >
                              <path
                                d="M280 0 C340 40 360 80 400 120"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                              <path
                                d="M120 200 C160 140 220 100 300 80"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                            </svg>

                                <div className="relative px-5 max-lg:px-2 py-2 flex flex-col justify-between h-28">
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm w-20 font-medium opacity-90">
                                      {getStatusText(statusName)}
                                    </div>

                                    {getStatusIcon(statusName)}
                                  </div>

                                  <div className="text-2xl flex justify-between items-center font-semibold">
                                    {formatCount(count)}
                                    {
                                      statusName == "retour" ?
                                      <div className="flex gap-2.5 justify-center items-center mt-2 text-xs max-lg:gap-1 max-lg:text-[11px]">
                                        <div className="flex gap-0.5 justify-center items-center"><ArrowDown className=" max-lg:w-3.5 max-lg:h-3.5 w-4 h-4" /> {formatCount(state.retoure_status.totalOrderretourne)}</div>
                                        <div className="flex gap-1 justify-center items-center"><ScanLine className=" max-lg:w-3.5 max-lg:h-3.5 w-3.5 h-3.5" /> {formatCount(state.retoure_status.totalOrderRetourneNoScane)}</div>
                                        <div className="flex gap-0.5 justify-center items-center"><Undo2 className=" max-lg:w-3.5 max-lg:h-3.5 w-4 h-4" /> {formatCount(state.retoure_status.totalOrderNoRetourne)}</div>
                                      </div>
                                      :
                                      ""
                                    }
                                  </div>
                                </div>
                              </Link>
                            
                          ))
                        }
                      </div>  
                      {
                        role === 1 || (role === 2 && roles.view_statistics === 1) ?
                        <div className={` grid max-[1450px]:grid-cols-2 gap-8 grid-cols-3 transition-all duration-700 delay-500 w-full ${
                          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}>
                          <div className="lg:col-span-2 space-y-6 max-[1450px]:col-span-2 ">
                            <div className="w-[100%] grid grid-cols-3 max-[1450px]:grid-cols-4 max-xl:grid-cols-2  max-lg:grid-cols-1 items-center gap-6">
                              <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">Pourcentage Livré</h3>
                                      <p className="text-[10px] text-gray-600">Indique des commandes livrées.</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="px-6 py-4">
                                  <RadialChart 
                                      percentage={Number(state.delivered_percentage)} 
                                      />  
                                </div>

                              </div>
                              <div className="w-[100%]  bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">Pourcentage Confirmé</h3>
                                      <p className="text-[10px] text-gray-600">Indique des commandes confirmées.</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="px-6 py-4">
                                  <RadialChart 
                                      percentage={Number(state.confirme_percentage)} 
                                      />  
                                </div>
                                </div> 
                                <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">Meilleurs confirmateurs</h3>
                                      <p className="text-[10px] text-gray-600">Indique des confirmateurs.</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="max-w-max max-h-max pb-4 pt-2">
                                  <BaarChartConfirmator data={state.top_confirmators} />
                                </div>

                                </div> 
                                <div className={`w-[100%] min-[1450px]:hidden  bg-gradient-to-r h-full from-[var(--color-primary-500)] to-[var(--color-primary-300)] rounded-xl shadow-lg text-white transition-all duration-500 hover:shadow-xl ${
                                    showContent ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                                  }`}
                                  style={{
                                    transitionDelay: showContent ? '1000ms' : '0ms'
                                  }}>
                                    <div className="p-6">
                                      <div className="flex items-center justify-between mb-4">
                                        <Activity className="h-8 w-8" />
                                        <Eye className="h-5 w-5 opacity-80" />
                                      </div>
                                      <h3 className="text-lg font-semibold mb-1">Live Activity</h3>
                                      <p className="text-blue-100 text-sm mb-4">Real-time user engagement</p>
                                      <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                          <span>Active Users</span>
                                          <span className="font-semibold">1,234</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                          <span>Page Views</span>
                                          <span className="font-semibold">5,678</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                          <span>Bounce Rate</span>
                                          <span className="font-semibold">23.4%</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                            </div>
                            <div className="w-[100%] max-h-max p-1 bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <BaarChart data={state.daily_orders} />
                            </div>

                            <div className="w-[100%] max-h-max p-1 bg-white  rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <LinneChart data={state.daily_revenue}  />
                            </div>

                          </div>
                          

                          <div className=" max-[1450px]:grid max-[1450px]:grid-cols-2 max-[1450px]:col-span-2 max-lg:grid-cols-1 max-[1450px]:gap-5 space-y-6">
                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">Répartition des commandes par ville</h3>
                                <p className="text-[10px] text-gray-600">Nombre de commandes par zone géographique</p>
                              </div>
                              <div className="">

                                <Map Data={state?.cities_orders}  />
                              </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">Top 5 des produits</h3>
                                <p className="text-[10px] text-gray-600">Produits les plus performants</p>
                              </div>
                              <div className="px-6 py-4">
                                {
                                  state.top_sellers?.length > 0 ? 
                                    <div className="space-y-3">
                                      {topProducts.map((product, index) => (
                                        <div 
                                          key={product.name + index} 
                                          className={`space-y-2 transition-all duration-500 ${
                                            showContent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                          }`}
                                          style={{
                                            transitionDelay: showContent ? `${index * 100 + 700}ms` : '0ms'
                                          }}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                              <div className="flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden">
                                                {product.image ? (
                                                  <img 
                                                    width="32"
                                                    height="32"
                                                    alt={product.name}
                                                    src={`${BASE_URL}/storage/${product.image}`}
                                                    className="object-cover"
                                                  />
                                                ) : (
                                                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                                                    <Package className="w-4 h-4 text-gray-400" />
                                                  </div>
                                                )}
                                              </div>
                                              <div>
                                                <p className="font-medium text-gray-900 text-xs">{product.name}</p>
                                                <p className="text-xs text-gray-500">{product.sales} ventes - {product.progress.toFixed(2)}%</p>
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <p className="font-bold text-green-600 text-xs">
                                                {showContent ? product.revenue : '0.00 MAD'}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                            <div
                                              className="bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)] h-2 rounded-full transition-all duration-1000 ease-out"
                                              style={{ 
                                                width: showContent ? `${product.progress}%` : '0%'
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  :
                                  <div className="h-10"></div>
                                }
                              </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">Top 5 des vendeurs</h3>
                                <p className="text-[10px] text-gray-600">Vendeurs les plus performants</p>
                              </div>
                              <div className="px-6 py-4">
                                {
                                  state.top_sellers?.length > 0 ? 
                                    <div className="space-y-3">
                                      {topSallers?.map((saller, index) => (
                                        <div 
                                          key={index} 
                                          className={`space-y-2 transition-all duration-500 ${
                                            showContent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                          }`}
                                          style={{
                                            transitionDelay: showContent ? `${index * 100 + 700}ms` : '0ms'
                                          }}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                              <div className="flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden">
                                                <div className="rounded-full p-1 bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)]">
                                                  <User className="w-5 h-5 text-white" />
                                                </div>
                                              </div>
                                              <div>
                                                <p className="font-medium text-gray-900 text-xs">
                                                  {saller.name}
                                                  <span className="font-100 text-gray-500 text-xs"> ({saller.name_store !== "" ? saller.name_store : ""})</span>
                                                </p>
                                                <p className="text-xs text-gray-500">{saller.total_orders} ventes - {saller.progress.toFixed(2)}%</p>
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <p className="font-bold text-green-600 text-xs">
                                                {showContent ? saller.revenue : '0.00 MAD'}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                            <div
                                              className="bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)] h-2 rounded-full transition-all duration-1000 ease-out"
                                              style={{ 
                                                width: showContent ? `${saller.progress}%` : '0%'
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    :
                                    <div className="h-10"></div>
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                        :
                        ''
                      }
                    </div>
                  }
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
